import type { FeatureId, ToolbarConfig } from './config';

export type Contrast = 'off' | 'high' | 'inverted' | 'grayscale';
export type Guide = 'off' | 'line' | 'mask';

export interface Prefs {
	fontSize: number;
	lineHeight: number;
	letterSpacing: number;
	wordSpacing: number;
	readableFont: boolean;
	dyslexiaFont: boolean;
	contrast: Contrast;
	highlightLinks: boolean;
	highlightHeadings: boolean;
	bigCursor: boolean;
	stopAnimations: boolean;
	hideImages: boolean;
	readingGuide: Guide;
	tts: boolean;
	keyboardHints: boolean;
}

export const FONT_SCALES = [ 1, 1.1, 1.25, 1.5, 1.75, 2 ];
export const LINE_HEIGHTS = [ 0, 1.5, 1.8, 2.2 ];
export const LETTER_SPACINGS = [ 0, 0.05, 0.1, 0.16 ];
export const WORD_SPACINGS = [ 0, 0.1, 0.2, 0.32 ];

export const defaultPrefs = ( reducedMotion: boolean ): Prefs => ( {
	fontSize: 0,
	lineHeight: 0,
	letterSpacing: 0,
	wordSpacing: 0,
	readableFont: false,
	dyslexiaFont: false,
	contrast: 'off',
	highlightLinks: false,
	highlightHeadings: false,
	bigCursor: false,
	stopAnimations: reducedMotion,
	hideImages: false,
	readingGuide: 'off',
	tts: false,
	keyboardHints: false,
} );

const level = ( v: unknown, max: number, fallback: number ): number =>
	typeof v === 'number' && Number.isInteger( v )
		? Math.max( 0, Math.min( max, v ) )
		: fallback;
const flag = ( v: unknown, fallback: boolean ): boolean =>
	typeof v === 'boolean' ? v : fallback;
const pick = < T extends string >(
	v: unknown,
	options: readonly T[],
	fallback: T
): T => ( options.includes( v as T ) ? ( v as T ) : fallback );

/**
 * Validate stored preferences; anything unknown or out of range falls back to the base value.
 * @param raw
 * @param base
 */
export const sanitizePrefs = ( raw: unknown, base: Prefs ): Prefs => {
	const r = ( raw && typeof raw === 'object' ? raw : {} ) as Record<
		string,
		unknown
	>;
	return {
		fontSize: level( r.fontSize, FONT_SCALES.length - 1, base.fontSize ),
		lineHeight: level(
			r.lineHeight,
			LINE_HEIGHTS.length - 1,
			base.lineHeight
		),
		letterSpacing: level(
			r.letterSpacing,
			LETTER_SPACINGS.length - 1,
			base.letterSpacing
		),
		wordSpacing: level(
			r.wordSpacing,
			WORD_SPACINGS.length - 1,
			base.wordSpacing
		),
		readableFont: flag( r.readableFont, base.readableFont ),
		dyslexiaFont: flag( r.dyslexiaFont, base.dyslexiaFont ),
		contrast: pick(
			r.contrast,
			[ 'off', 'high', 'inverted', 'grayscale' ] as const,
			base.contrast
		),
		highlightLinks: flag( r.highlightLinks, base.highlightLinks ),
		highlightHeadings: flag( r.highlightHeadings, base.highlightHeadings ),
		bigCursor: flag( r.bigCursor, base.bigCursor ),
		stopAnimations: flag( r.stopAnimations, base.stopAnimations ),
		hideImages: flag( r.hideImages, base.hideImages ),
		readingGuide: pick(
			r.readingGuide,
			[ 'off', 'line', 'mask' ] as const,
			base.readingGuide
		),
		tts: flag( r.tts, base.tts ),
		keyboardHints: flag( r.keyboardHints, base.keyboardHints ),
	};
};

/**
 * Preferences for features the admin switched off never take effect, even if stored.
 * @param p
 * @param cfg
 */
export const effectivePrefs = ( p: Prefs, cfg: ToolbarConfig ): Prefs => {
	const on = ( id: FeatureId ) => cfg.features[ id ];
	const base = defaultPrefs( false );
	return {
		fontSize: on( 'font_size' ) ? p.fontSize : base.fontSize,
		lineHeight: on( 'line_height' ) ? p.lineHeight : base.lineHeight,
		letterSpacing: on( 'letter_spacing' )
			? p.letterSpacing
			: base.letterSpacing,
		wordSpacing: on( 'word_spacing' ) ? p.wordSpacing : base.wordSpacing,
		readableFont: on( 'readable_font' ) && p.readableFont,
		dyslexiaFont: on( 'dyslexia_font' ) && p.dyslexiaFont,
		contrast:
			( p.contrast === 'high' && on( 'contrast_high' ) ) ||
			( p.contrast === 'inverted' && on( 'contrast_inverted' ) ) ||
			( p.contrast === 'grayscale' && on( 'contrast_grayscale' ) )
				? p.contrast
				: 'off',
		highlightLinks: on( 'highlight_links' ) && p.highlightLinks,
		highlightHeadings: on( 'highlight_headings' ) && p.highlightHeadings,
		bigCursor: on( 'big_cursor' ) && p.bigCursor,
		stopAnimations: on( 'stop_animations' ) && p.stopAnimations,
		hideImages: on( 'hide_images' ) && p.hideImages,
		readingGuide: on( 'reading_guide' ) ? p.readingGuide : 'off',
		tts: on( 'text_to_speech' ) && p.tts,
		keyboardHints: on( 'keyboard_hints' ) && p.keyboardHints,
	};
};
